import $ from "jquery";
import * as _ from "underscore";
import config from "flow/config";
import manifestManager from "flow/utils/manifestManager";
import hashQuery from "flow/utils/hashQuery";
import baseUrl from "flow/wrappers/baseUrl";
import protect from "flow/protect";
import flow3NavigationHelper from "flow/utils/flow3NavigationHelper";
import queryHelper from "flow/init/queryHelper";
import { Dictionary, ViewContext } from "flow/api/types";
import MyFlow from "flow/MyFlow";
import Component from "flow/Component";
import { UserContextTabs } from "flow/viewModels/userContextTabs";
import Viewport from "flow/Viewport";
import { WorkingContext } from "flow/viewModels/workingContext";
import { Router } from "flow/router";
import { Dialog } from "flow/utils/dialog";
import { SideTabs } from "flow/viewModels/sideTabs";
import { Menu } from "flow/viewModels/menu";
import { CanLeaveEntry } from "flow/Viewport";
import environment from "flow/configuration/environment";

let viewport: Viewport;
let menu: Menu;
let router: Router;
let dialog: Dialog;
let workingContext: WorkingContext;
let userContextTab: UserContextTabs;
let myFlow: MyFlow;
let sideTab: SideTabs;

function init(
    vp: Viewport,
    menuVm: Menu,
    r: Router,
    d: Dialog,
    wc: WorkingContext,
    uCT: UserContextTabs,
    mf: MyFlow,
    st: SideTabs
) {
    viewport = vp;
    menu = menuVm;
    router = r;
    dialog = d;
    workingContext = wc;
    userContextTab = uCT;
    myFlow = mf;
    sideTab = st;
}

function openEmptyWindow(windowTitle?: string, windowFeatures?: string) {
    const args = [""];

    if (arguments.length > 0) {
        args.push(windowTitle);
    }

    if (arguments.length > 1) {
        args.push(windowFeatures);
    }

    return window.open.apply(window, args as any);
}

function activateById(id: string, viewContext: Dictionary, selectedContainerId: string) {
    return activateByIdInternal(id, viewContext, selectedContainerId);
}

function activateByIdInNewTab(id: string, viewContext: Dictionary, selectedContainerId: string) {
    let url;
    if (manifestManager.isFlow3Context(id)) {
        return openFlow3ContextInNewTab(id, viewContext, selectedContainerId);
    }

    if (manifestManager.isFlow3FunctionContainer(id)) {
        return openFlow3ComponentInNewTab(id, viewContext);
    }

    if (manifestManager.isHome(id)) {
        url =
            baseUrl +
            queryHelper.generateProductConfigParams(true) +
            hashQuery.makeViewContextStringForWorkingContext(
                config.componentIds.homeContext,
                viewContext,
                selectedContainerId
            );
    } else if (manifestManager.isWorkingContext(id)) {
        url =
            baseUrl +
            queryHelper.generateProductConfigParams(true) +
            hashQuery.makeViewContextStringForWorkingContext(id, viewContext, selectedContainerId);
    } else if (manifestManager.isFunction(id)) {
        url =
            baseUrl +
            queryHelper.generateProductConfigParams(true) +
            hashQuery.makeViewContextStringForDefaultWorkingContext(id, viewContext);
    } else {
        // Ignore dialogs and menus
        return undefined;
    }

    const windowHandle = window.open();
    windowHandle.opener = null;
    windowHandle.location.href = url;
}

function activateByIdBesideExistingContainer(component: Component, id: string, viewContext?: Dictionary) {
    if (manifestManager.isContextContainer(id) && manifestManager.isContextContainer(component.manifest.id)) {
        const manifest = manifestManager.getManifestWithId(id);
        return viewport.addComponentBesideExisting(component, manifest, viewContext);
    }

    return false;
}

function activateByIdInternal(id: string, viewContext?: Dictionary, selectedContainerId?: string) {
    if (manifestManager.isFlow3Context(id)) {
        return openFlow3ContextInNewTab(id, viewContext, selectedContainerId);
    }

    if (manifestManager.isFlow3FunctionContainer(id)) {
        return openFlow3ComponentInNewTab(id, viewContext);
    }
    if (canLeaveSweepWithUserFeedback(id, undefined, viewContext) === false) {
        return false;
    }

    if (manifestManager.isHome(id)) {
        myFlow.publish(config.topics.navigationPanelItemSelected, id);
        router.navigateHome(undefined, undefined);
        return undefined;
    }

    const manifest = manifestManager.getManifestWithId(id);

    if (!manifest) {
        myFlow.warn(`No manifest found for id: ${id}`);
        return undefined;
    }

    if (manifestManager.isWorkingContext(id)) {
        router.navigateToWorkingContext(id, viewContext, selectedContainerId);
    } else if (manifestManager.isMenu(id)) {
        myFlow.publish(config.topics.navigationPanelItemSelected, id);
        const extendedViewContext = $.extend({}, manifest.defaultParameters, viewContext);
        menu.loadComponentById(id, extendedViewContext, !viewContext);
    } else if (manifestManager.isUserContextTab(id)) {
        userContextTab.loadComponentById(id, viewContext);
    } else if (manifestManager.isSideTab(id)) {
        viewContext = viewContext == undefined ? {} : viewContext;
        const extendedViewContext = $.extend({}, manifest.defaultParameters, viewContext);
        sideTab.loadComponentById(id, extendedViewContext);
    } else if (manifestManager.isComponent(id)) {
        if (manifest == null) {
            return undefined;
        } else if (manifestManager.isExternalComponent(id)) {
            return window.open(createExternalComponentUrlForId(id, viewContext));
        } else {
            if (manifest.hasNoWorkingContext) {
                const currentViewContext = router.getCurrentFunctionViewContext();

                viewContext = $.extend({}, manifest.defaultParameters, viewContext);

                if (
                    workingContext.currentFunctionComponentId() !== manifest.id ||
                    !_.isEqual(currentViewContext, viewContext)
                ) {
                    router.navigateApp(id, viewContext);
                } else {
                    viewport.resetWorkingContextComponents();
                    myFlow.publish(config.topics.navigationPanelItemSelected, id);
                    viewport.addComponentLast(manifest, viewContext);
                    selectById(id);
                }
            } else {
                viewport.addComponentLast(manifest, viewContext);
            }
        }
    }

    return undefined;
}

function deactivateById(id: string) {
    if (manifestManager.isWorkingContext(id)) {
        router.navigateApp(undefined, undefined);

        if (viewport.components().length == 0) {
            router.navigateHome(undefined, undefined);
        }
    } else if (manifestManager.isComponent(id)) {
        const matchingAppComponents = $.grep(viewport.components(), function (c) {
            return c.manifest.id === id;
        });
        if (matchingAppComponents.length > 0) {
            matchingAppComponents.forEach(component => {
                viewport.removeComponent(component);
            });
        }

        if (viewport.components().length == 0) {
            router.navigateHome(undefined, undefined);
        }
    }
}

function deactivateInstance(component: Component) {
    if (manifestManager.isComponent(component.manifest.id)) {
        viewport.removeComponent(component);
    } else {
        deactivateById(component.manifest.id);
    }
}

function activateAndSelectById(id: string, viewContext: ViewContext) {
    if (activateById(id, viewContext, undefined) === false) return;

    if (manifestManager.isComponent(id)) {
        selectById(id);
    }
}

function openFlow3ComponentInNewTab(id: string, viewContext?: Dictionary) {
    const manifest = manifestManager.getManifestWithId(id);
    viewContext = $.extend({}, manifest.defaultParameters, viewContext);
    const url = flow3NavigationHelper.generateHashForFunction(manifest.productId, manifest.payload, id, viewContext);
    const windowHandle = window.open();
    windowHandle.opener = null;
    windowHandle.location.href = url;
}

function openFlow3ContextInNewTab(id: string, viewContext: Dictionary, selectedContainerId: string) {
    const manifest = manifestManager.getManifestWithId(id);
    viewContext = $.extend({}, manifest.defaultParameters, viewContext);
    const url = flow3NavigationHelper.generateHashForContext(
        manifest.productId,
        manifest.payload,
        id,
        viewContext,
        selectedContainerId
    );
    const windowHandle = window.open();
    windowHandle.opener = null;
    windowHandle.location.href = url;
}

function activateExistingExternalComponent(componentWindow: Window, id: string, viewContext?: Dictionary) {
    componentWindow.location = createExternalComponentUrlForId(id, viewContext);
}

function createExternalComponentUrlForId(id: string, viewContext?: Dictionary) {
    const manifest = manifestManager.getManifestWithId(id);
    const url = manifest.absoluteUrl ? manifest.absoluteUrl : manifest.baseUrl + manifest.relativeUrl;

    const cacheBuster = manifest.includeCacheBusterAsQueryParam
        ? manifestManager.getCacheBusterForModule(manifest.moduleId)
        : undefined;

    const viewContextParams = hashQuery.makeViewContextStringForExternalComponent(viewContext, cacheBuster);

    return url + viewContextParams;
}

function selectById(id: string, filter?: (hint?: any) => boolean, skipAnimation?: boolean) {
    return viewport.selectById(id, filter, skipAnimation);
}

function selectByInstance(component: Component) {
    return viewport.selectByInstance(component);
}

function selectFirst(skipAnimation: boolean) {
    return viewport.selectFirst(skipAnimation);
}

function showErrorDialog(myFlowC: MyFlow, title: string, message: string, showCorrelationOnly: boolean) {
    return dialog.showErrorDialog(myFlowC, title, message, showCorrelationOnly);
}

function showInformationDialog(myFlowC: MyFlow, title: string, message: string) {
    return dialog.showInformationDialog(myFlowC, title, message);
}

function showConfirmDialog(
    myFlowC: MyFlow,
    title: string,
    message: string,
    buttonText1: string,
    buttonText2: string,
    buttonText3: string
) {
    return dialog.showConfirmDialog(myFlowC, title, message, buttonText1, buttonText2, buttonText3);
}

function showCustomDialog<T>(myFlowC: MyFlow, id: string, viewContext: Dictionary) {
    return dialog.showCustomDialog<T>(myFlowC, id, viewContext);
}

function collectCanLeaveResults(id: string): CanLeaveEntry[] {
    const canLeaveResponses: any[] = [];
    return canLeaveResponses
        .concat(canLeaveViewport(id))
        .concat(menu.canLeave())
        .concat(workingContext.canLeave(id))
        .concat(sideTab.canLeave());
}

function selectFirstContainer(dirtyComponents: CanLeaveEntry[]) {
    const someoneDirtyHasFocus = dirtyComponents.some(dc => dc.component === viewport.selectedComponent());

    if (someoneDirtyHasFocus) {
        return; // Let them keep it :)
    }

    const sortedDirtyContainers = dirtyComponents
        .map(dc => dc.component)
        .filter(c => c.viewModel().performCustomCanLeave === undefined)
        .sort((a, b) => a.order.compare(b.order));

    const firstContainer = sortedDirtyContainers.length > 0 ? sortedDirtyContainers[0] : undefined;

    if (firstContainer) {
        selectByInstance(firstContainer);
    }
}

function canLeaveSweepWithUserFeedback(
    id?: string,
    excludeComponent?: Component,
    viewContext?: Dictionary,
    stepIndex?: number
) {
    return canLeaveSweepInternal(id, true, excludeComponent, viewContext, stepIndex);
}

function canLeaveSweepWithoutUserFeedback(id?: string, excludeComponent?: Component, stepIndex?: number) {
    return canLeaveSweepInternal(id, false, excludeComponent, undefined, stepIndex);
}

function canLeaveSweepInternal(
    id?: string,
    showNotifications?: boolean,
    excludeComponent?: Component,
    viewContext?: Dictionary,
    stepIndex?: number
) {
    let canLeaveFlag = true;
    if (id === undefined || manifestManager.mightCauseNavigation(id)) {
        const canLeaveResult = collectCanLeaveResults(id);
        if (canLeaveResult.length > 0) {
            const resultsWithoutExcludedComponent = canLeaveResult.filter(r => r.component !== excludeComponent);
            if (showNotifications) {
                const componentsWithCustomCanLeave = resultsWithoutExcludedComponent
                    .map(r => r.component)
                    .filter(dc => dc.viewModel() && dc.viewModel().performCustomCanLeave);

                if (componentsWithCustomCanLeave.length > 0) {
                    const component = componentsWithCustomCanLeave[0];
                    selectByInstance(component); //show user which dirty view the performCustomCanLeave concerns
                    //passing on id and viewContext so that performCustomCanLeave can continue navigation if necessary
                    protect(component.myFlow, component.viewModel(), component.viewModel().performCustomCanLeave)(
                        id,
                        viewContext,
                        stepIndex
                    );
                    //exiting the activation process because conditional navigation continues through performCustomCanLeave
                    canLeaveFlag = false;
                } else {
                    resultsWithoutExcludedComponent.forEach(r =>
                        r.component.myFlow.notifyInfo(r.result, r.component.manifest.name)
                    );
                }

                selectFirstContainer(canLeaveResult);
            } else if (resultsWithoutExcludedComponent.length > 0) {
                canLeaveFlag = false;
            }
        }
    }

    return canLeaveFlag;
}

function canLeaveViewport(id: string) {
    return viewport.canLeave(id);
}

function isComponentActive(id: string, filter?: (hint?: any) => boolean) {
    return viewport.isComponentVisible(id, filter) === true;
}

function getSelectedContainerId(): string {
    return viewport.selectedContainerId();
}

function refreshFlow(ignoreDirtyCheck?: boolean, stayInPlace?: boolean) {
    if (ignoreDirtyCheck) {
        environment.set(environment.keys.ignorePageUnloadDirtyCheck, true);
    }

    if (stayInPlace) {
        router.reload();
    } else {
        router.reloadHome();
    }
}

function reactivateWorkingContext() {
    router.reactivateWorkingContext();
}

function replaceViewContext(workingContextId: string, viewContext: Dictionary, selectedContainerId: string) {
    router.replaceViewContextAndThenRoute(workingContextId, viewContext, selectedContainerId);
}

function getCurrentViewContext() {
    return router.getCurrentFunctionViewContext();
}

function goBack() {
    if (!canLeaveSweepWithUserFeedback()) {
        return;
    }

    router.navigateBack();
}

function gotoStep(stepIndex: number, force: boolean) {
    if (workingContext.isWorkflow()) {
        viewport.workflow.gotoStep(stepIndex, force);
    }
}

function adjustViewContextInUrl(newViewContext: Dictionary) {
    router.adjustViewContextInUrl(newViewContext);
}

function updateSelectedContainerInUrl(selectedContainerId: string) {
    router.updateSelectedContainerInUrl(selectedContainerId);
}

export default {
    init: init,
    openEmptyWindow: openEmptyWindow,
    activateById: activateById,
    activateByIdBesideExistingContainer: activateByIdBesideExistingContainer,
    activateByIdInNewTab: activateByIdInNewTab,
    selectById: selectById,
    selectByInstance: selectByInstance,
    selectFirst,
    activateAndSelectById: activateAndSelectById,
    activateExistingExternalComponent: activateExistingExternalComponent,
    showErrorDialog: showErrorDialog,
    showInformationDialog: showInformationDialog,
    showConfirmDialog: showConfirmDialog,
    showCustomDialog: showCustomDialog,
    deactivateById: deactivateById,
    deactivateInstance: deactivateInstance,
    collectCanLeaveResults: collectCanLeaveResults,
    canLeaveSweepWithUserFeedback: canLeaveSweepWithUserFeedback,
    canLeaveSweepWithoutUserFeedback: canLeaveSweepWithoutUserFeedback,
    isComponentActive: isComponentActive,
    getSelectedContainerId: getSelectedContainerId,
    refreshFlow: refreshFlow,
    reactivateWorkingContext: reactivateWorkingContext,
    replaceViewContext: replaceViewContext,
    getCurrentViewContext: getCurrentViewContext,
    goBack: goBack,
    gotoStep: gotoStep,
    adjustViewContextInUrl: adjustViewContextInUrl,
    updateSelectedContainerInUrl
};
